import { Building, ChevronDown, ExternalLink, Linkedin, MapPin, Users } from 'lucide-react'
import Image from 'next/image'
import { useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import { linkedinSearchOrganization } from 'sprites-types/workflow/tools/linkedinSearchOrganization'
import { ContextMenuWrapper } from '../shared/ContextMenuWrapper'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

function OrganizationItem({ org, className }: { org: any; className?: string }) {
  const name = org.name || org.companyName || org.organizationName || 'Company'
  const description = org.description || org.tagline || org.summary || ''
  const industry = org.industry || org.industries?.[0] || ''
  const employees = org.employeeCount || org.employeeCountRange || org.size || ''
  const location = org.location || org.headquarters || org.city || ''
  const website = org.website || org.websiteUrl || org.url
  const logo = org.logo || org.logoUrl || org.profilePicture
  const linkedinUrl =
    org.linkedinUrl ||
    org.publicUrl ||
    `https://linkedin.com/company/${org.id || org.universalName}`
  const followers = org.followerCount || org.followersCount || 0

  const formatNumber = (num: number) => {
    if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
    return num.toLocaleString()
  }

  return (
    <div
      className={`rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-sm ${className}`}
    >
      <div className="px-4 py-3">
        <div className="flex items-start gap-3">
          {logo ? (
            <Image
              src={logo}
              alt={`${name} logo`}
              width={48}
              height={48}
              className="h-12 w-12 rounded-lg bg-white object-cover"
            />
          ) : (
            <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-100">
              <Building className="h-6 w-6 text-blue-600" />
            </div>
          )}

          <div className="min-w-0 flex-1">
            <div className="mb-1 text-sm font-semibold text-gray-900">{name}</div>

            {description && (
              <div className="mb-2 line-clamp-2 text-xs text-gray-700">{description}</div>
            )}

            <div className="mb-2 flex flex-wrap items-center gap-3 text-xs text-gray-600">
              {industry && (
                <div className="flex items-center gap-1">
                  <span>{industry}</span>
                </div>
              )}
              {employees && (
                <div className="flex items-center gap-1">
                  <Users className="h-3 w-3" />
                  <span>{employees} employees</span>
                </div>
              )}
              {location && (
                <div className="flex items-center gap-1">
                  <MapPin className="h-3 w-3" />
                  <span>{location}</span>
                </div>
              )}
            </div>

            {followers > 0 && (
              <div className="mb-2 text-xs text-gray-600">{formatNumber(followers)} followers</div>
            )}

            <div className="flex items-center gap-3">
              {website && (
                <a
                  href={website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
                >
                  <ExternalLink className="h-3 w-3" />
                  Website
                </a>
              )}
              <a
                href={linkedinUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
              >
                <Linkedin className="h-3 w-3" />
                View Profile
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function LinkedinSearchOrganizationDisplayComponent({ nodeExecution }: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(linkedinSearchOrganization, nodeExecution.inputs)
  const [isExpanded, setIsExpanded] = useState(true)

  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data
  const inputs = nodeExecution.inputs

  const query = inputs.query || inputs.search || inputs.keywords || 'search'
  const organizations = Array.isArray(result)
    ? result
    : (result as any)?.data ||
      (result as any)?.organizations ||
      (result as any)?.companies ||
      (result ? [result] : [])

  if (organizations.length === 0) {
    return (
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="flex-shrink-0">
            <Building className="h-5 w-5 text-gray-400" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-gray-900">No organizations found</div>
            <div className="text-xs text-gray-600">Search: &quot;{query}&quot;</div>
          </div>
        </div>
      </div>
    )
  }

  if (organizations.length === 1) {
    return <OrganizationItem org={organizations[0]} />
  }

  return (
    <div className="space-y-3">
      <ContextMenuWrapper actions={[]}>
        <div className="overflow-hidden rounded-3xl border border-gray-200/50 bg-gray-200/70 text-black shadow-sm">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="group flex w-full items-center gap-4 bg-white px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
            aria-expanded={isExpanded}
          >
            <div className="flex-shrink-0">
              <Linkedin className="h-5 w-5 text-blue-700" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold text-gray-900">
                LinkedIn Organization Search
              </div>
              <div className="text-xs font-medium tracking-wide text-gray-500">
                {organizations.length} companies found • &quot;{query}&quot;
              </div>
            </div>
            <div className="flex-shrink-0">
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </div>
          </button>
        </div>
      </ContextMenuWrapper>

      {isExpanded && (
        <div className="space-y-2">
          {organizations.map((org: any, index: number) => (
            <OrganizationItem
              key={org.id || org.universalName || index}
              org={org}
              className="mx-8"
            />
          ))}
        </div>
      )}
    </div>
  )
}

function downloadLinkedinSearchOrganization(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function linkedinSearchOrganizationToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(linkedinSearchOrganization, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data
  const inputs = nodeExecution.inputs

  const query = inputs.query || inputs.search || inputs.keywords || 'search'
  const organizations = Array.isArray(result)
    ? result
    : (result as any)?.data ||
      (result as any)?.organizations ||
      (result as any)?.companies ||
      (result ? [result] : [])

  if (organizations.length === 0) {
    return `## LinkedIn Organization Search

**Query:** "${query}"

No organizations found.

---
`
  }

  const tableHeader = `| Company | Industry | Employees | Location | Followers | Website | LinkedIn |
|---------|----------|-----------|----------|-----------|---------|----------|`

  const tableRows = organizations
    .map((org: any) => {
      const name = org.name || org.companyName || org.organizationName || 'Company'
      const industry = org.industry || org.industries?.[0] || '-'
      const employees = org.employeeCount || org.employeeCountRange || org.size || '-'
      const location = org.location || org.headquarters || org.city || '-'
      const followers = org.followerCount || org.followersCount || 0
      const followersDisplay = followers > 0 ? followers.toLocaleString() : '-'
      const website = org.website || org.websiteUrl || org.url
      const websiteDisplay = website ? `[Visit](${website})` : '-'
      const linkedinUrl =
        org.linkedinUrl ||
        org.publicUrl ||
        `https://linkedin.com/company/${org.id || org.universalName}`
      const linkedinDisplay = `[View](${linkedinUrl})`

      return `| ${name} | ${industry} | ${employees} | ${location} | ${followersDisplay} | ${websiteDisplay} | ${linkedinDisplay} |`
    })
    .join('\n')

  const detailedDescriptions = organizations
    .filter((org: any) => (org.description || org.tagline || org.summary)?.length > 50)
    .slice(0, 5) // Limit to first 5 detailed descriptions
    .map((org: any) => {
      const name = org.name || org.companyName || org.organizationName || 'Company'
      const description = org.description || org.tagline || org.summary || ''
      return `### ${name}\n\n${description}`
    })
    .join('\n\n')

  return `## LinkedIn Organization Search (${organizations.length} companies)

**Query:** "${query}"

${tableHeader}
${tableRows}

${detailedDescriptions ? `\n## Company Descriptions\n\n${detailedDescriptions}\n` : ''}
---
`
}

export const LinkedinSearchOrganizationDisplay = createWorkflowToolComponent(
  LinkedinSearchOrganizationDisplayComponent,
  downloadLinkedinSearchOrganization,
  linkedinSearchOrganizationToMarkdown
)
